/**
 * useLiveUpdates — subscribe to /api/ws/live and invoke callback on message.
 * Auto-reconnect with exponential backoff. Silent-fail on any error so it
 * never crashes a page.
 */
import { useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pmv5_csuite_token";

export function useLiveUpdates(onEvent) {
  const attemptRef = useRef(0);
  const wsRef = useRef(null);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    if (!BACKEND_URL) return;
    let stopped = false;
    let reconnectTimer = null;

    const scheme = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const host = BACKEND_URL.replace(/^https?:\/\//, "");

    const connect = () => {
      if (stopped) return;
      const token = (() => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } })();
      if (!token) return;  // Not logged in — skip.

      try {
        const url = `${scheme}://${host}/api/ws/live?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => { attemptRef.current = 0; };
        ws.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (payload && payload.type && payload.type !== "hello") {
              cbRef.current?.(payload);
            }
          } catch { /* ignore malformed frames */ }
        };
        ws.onerror = () => { /* silent — onclose handles reconnect */ };
        ws.onclose = () => {
          if (stopped) return;
          const attempt = ++attemptRef.current;
          const delay = Math.min(30_000, 1000 * Math.pow(2, attempt));
          reconnectTimer = setTimeout(connect, delay);
        };
      } catch {
        /* schedule a retry so browsers that block WS fall back gracefully */
        reconnectTimer = setTimeout(connect, 5_000);
      }
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, []);
}
